/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
import React, { useEffect, useState } from 'react';
import { C, FONTE_MONO, FONTE_TEXTO, TONES, TONE_DO_STATUS } from '../paleta';
import { ordenarParaExibicao, ST_META } from '../utils/ordemStatus';
import { ROTULO_MACRO, tituloFalhasAnteriores } from '../utils/vocabularioMacro';
import { diaDaFalha } from '../utils/dataFalha';
import type { IEmpresaComStatus } from '../utils/derivarProcessos';
import { statusEfetivo } from '../utils/derivarResumo';
import type { StatusEsteira } from '../api';
import { pilulasDoPopup } from '../utils/pilulasPopup';
import { alternarStatusNaLista, filtrarPorStatusEfetivo } from '../utils/filtrosCompartilhados';
import type { AlvoAuditoria } from '../utils/erroDoPopup';
import { NomeEmpresa } from './NomeEmpresa';
import { HistoricoDaUnidade } from './HistoricoDaUnidade';
import type { NaoChegaram } from '../utils/naoPassaram';
import type { ParcelasDoNo } from '../utils/parcelasDoNo';
import { chaveDoErro, mensagemDoErro, topErros } from '../utils/topErros';
import { BotaoVerPrint } from './BotaoVerPrint';
import {
  categoriaDoItem,
  textoPct,
  type CategoriaPainel,
  type FunilDoNo,
  type PainelDoNo,
} from '../utils/painelDoNo';

/**
 * Uma ABA do popup analítico (13/10): a etapa inteira ou um RPA dela.
 *
 * Pedido do dono: *"as etapas que têm mais de um bot, me separar por abas os ok
 * e erro que existem em cada bot e o que falta passar ainda em cada bot"*.
 */
export interface AbaDoPainel {
  id: string;
  rotulo: string;
  titleTooltip?: string;
  painel: PainelDoNo;
  /** Quem tem registro nesta aba — as linhas ricas (mensagem, histórico). */
  companies: IEmpresaComStatus[];
  /** Funil contra a etapa/RPA anterior; `null` quando não há anterior. */
  funil: FunilDoNo | null;
}

/** Qual lista o popup analítico está mostrando. `null` = todas com registro. */
export type CategoriaDoPopup = CategoriaPainel | 'naoSeguiram' | 'foraDoUniverso';

export interface PainelDoPopup {
  abas: AbaDoPainel[];
  /** codi_emp → nome, para listar quem NÃO tem registro (não está em `companies`). */
  nomes: Map<string, string>;
  /** "09/2026" — a competência do movimento. */
  competencia: string;
  categoriaInicial: CategoriaDoPopup | null;
  /** Abre já recortado para quem faturou ('com') ou não ('sem'). */
  movimentoInicial?: 'com' | 'sem' | null;
  comMovimento: Set<string> | null;
  /** Empresas em erro na etapa que concluíram em PARTE dos RPAs dela. */
  erroParcial: string[];
}

/**
 * Popup de Processos — espelho 1:1 do modal do protótipo (`sistema/index.html`
 * linhas 311-378) com a lógica de `procPopupView()`/`popupSummary()` portada:
 * pílulas-resumo só com ≥2 empresas, lista ordenada por status, caixa de mensagem
 * de erro/aviso, estado vazio de contagem e ação de navegação no rodapé.
 *
 * Correção de 11/09: a lista usa `ordenarParaExibicao` (falha/alerta primeiro), não
 * mais `ordenarPorStatus` (a ordem de DEDUP, com `done` no topo). Com 282 concluídas
 * a primeira falha caía na posição 283 e o popup parecia não ter mais mensagem de
 * erro — ver o comentário das duas ordens em `utils/ordemStatus`.
 *
 * Evolução de 08/09 sobre o template: as pílulas-resumo viraram TOGGLES de
 * multi-seleção que filtram a lista DO PRÓPRIO popup (pílula ativa = preenchida
 * com ring, padrão das pílulas de status da Fase 9). As contagens seguem TOTAIS
 * (descrevem o nó); o estado é LOCAL — fechar/reabrir o popup reseta — e COMPÕE
 * com o filtro global de status (a lista já chega globalmente filtrada, o local
 * só refina). Derivação das pílulas em `utils/pilulasPopup`.
 *
 * Evolução de 09/09: as caixas de erro/aviso viraram ATALHO para a aba Erros —
 * clicar na mensagem fecha o popup e abre a auditoria já filtrada naquela empresa
 * + bot + status (ver `utils/erroDoPopup`). É um botão de verdade (teclado inclusive),
 * não uma `div` com onClick: a mensagem é o gatilho mais óbvio de "quero ver isso
 * por inteiro" e precisa ser alcançável por Tab.
 *
 * Revisão 09/09: quando a dedup do grupo elege OUTRO status (done vence err) a linha
 * fica sem caixa de mensagem e perdia o atalho — o caminho mais provável de
 * investigação. Nesse caso entra só o link discreto "ver na auditoria →", que filtra
 * por empresa + status SEM bot; nada mais do popup muda.
 */

export interface PopupDados {
  title: string;
  /** Tooltip do título — o NOME REAL do bot quando `title` é um apelido de exibição. */
  titleTooltip?: string;
  sub: string;
  companies: IEmpresaComStatus[] | null;
  count: number | null;
  actionLabel?: string;
  onAction?: () => void;
  /**
   * Nome REAL do bot do contexto do popup (nano/board), usado quando o item da
   * lista não carrega o seu próprio (listas da execução). Popup de grupo não
   * define: filtrar por um bot arbitrário do grupo esconderia a ocorrência.
   */
  botPadrao?: string;
  /**
   * `true` no popup aberto da MACRO: erro e alerta viram "Falha de
   * processamento" e a linha mostra AS DUAS mensagens quando existem (medido em
   * 24/09: 143 pares empresa/bot têm as duas). Board e nano seguem com a caixa
   * do status, onde a distinção erro/alerta é o que se investiga.
   */
  macro?: boolean;
  /**
   * "NÃO CHEGARAM A ESTA ETAPA" (07/10, pedido do dono): quem está na base da
   * comparação — o fluxo da área, na macro; o bot anterior, no micro — e não
   * tem log aqui.
   *
   * Vem PRONTO de quem abre o popup: o popup é dumb — não sabe qual nó o abriu
   * nem quem são os vizinhos na cadeia, e passar o grupo inteiro aqui só para
   * ele refazer a conta criaria uma segunda régua para a mesma pergunta.
   *
   * Ausente = a cobertura não respondeu e a seção some. Nunca uma lista vazia
   * significando "todas chegaram" — ver `NaoChegaramNoNo`.
   */
  naoChegaram?: NaoChegaram | null;
  /**
   * AS TRÊS PARCELAS DO NÓ que abriu este popup — a régua de `parcelasDoNo`.
   *
   * Existe porque o popup e o nó mediam o MESMO `↳` com bases diferentes
   * (conferido nó a nó em 09/10, nos 6 nós do Fiscal LP): o nó usa o universo do
   * departamento (`area.universo`, 345) e a seção daqui imprimia a união dos
   * agrupamentos (`naoChegaram.base`, 344), então o popup dizia sempre 1 a menos
   * que a bolha ao lado — "↳ 89" contra "↳ 90" em Auditor Notas Fiscais LP.
   *
   * Com as parcelas vindas prontas, a mesma pergunta tem UM número nas duas
   * superfícies. `undefined` quando a cobertura não respondeu: aí a seção cai na
   * base publicada em `naoChegaram`, que é a única resposta disponível.
   *
   * NÃO alimenta as PÍLULAS, de propósito — ver `pilulasDoPopup`: elas são
   * toggles que recortam `companies`, e um número que não casa com a lista que
   * ele filtra abre o popup vazio. As pílulas descrevem a lista; as parcelas
   * descrevem o nó, e a nota de rodapé da seção liga as duas.
   */
  parcelas?: ParcelasDoNo | null;
  /**
   * POPUP ANALÍTICO (13/10): com ele o popup fica largo, ganha abas por RPA e os
   * cartões de parcela (ok · erro · em andamento · não constam · sem movimento ·
   * funil), cada um abrindo a própria lista. `naoChegaram`/`parcelas` e as
   * pílulas de status não são usados nesse modo — os cartões respondem as
   * mesmas perguntas pela régua única de `painelDoNo`.
   */
  painel?: PainelDoPopup | null;
}

/**
 * Normaliza para a busca do popup: sem acento, sem caixa, sem pontuação do
 * código. Quem digita "sao paulo" tem que achar "SÃO PAULO", e quem digita
 * "1.083" tem que achar "1083".
 */
const normalizarBusca = (texto: string): string =>
  (texto ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[.\-/]/g, '')
    .toLowerCase()
    .trim();

/**
 * Tons das caixas — o vermelho do erro e o âmbar do aviso do protótipo, e o azul
 * da pendência (28/09).
 *
 * O azul é o de matiz 250, o MESMO da fatia de pendentes na barra do nó
 * (`NoBoard`): a caixa e a fatia falam da mesma empresa e têm que parecer a
 * mesma coisa. Não é o azul de matiz 265 — esse é o de "Executando", e uma
 * pendente pintada com ele pareceria estar rodando.
 *
 * Exportado para o teste prender as cores: o jsdom descarta `oklch` do `style`,
 * então pelo DOM a troca de 250 por 265 passaria calada.
 */
export const TOM_CAIXA = {
  erro: {
    borda: 'oklch(0.85 0.08 28)',
    fundo: 'oklch(0.97 0.02 28)',
    fundoCabecalho: 'oklch(0.95 0.04 28)',
    fundoHover: 'oklch(0.945 0.035 28)',
    tintaCabecalho: 'oklch(0.5 0.16 28)',
    tinta: 'oklch(0.4 0.1 28)',
    icone: '✕',
    rotulo: 'Mensagem de erro',
  },
  aviso: {
    borda: 'oklch(0.85 0.1 75)',
    fundo: 'oklch(0.97 0.03 75)',
    fundoCabecalho: 'oklch(0.95 0.05 75)',
    fundoHover: 'oklch(0.945 0.045 75)',
    tintaCabecalho: 'oklch(0.5 0.12 75)',
    tinta: 'oklch(0.42 0.09 75)',
    icone: '⚠',
    rotulo: 'Aviso',
  },
  pendente: {
    borda: 'oklch(0.85 0.06 250)',
    fundo: 'oklch(0.97 0.015 250)',
    fundoCabecalho: 'oklch(0.95 0.03 250)',
    fundoHover: 'oklch(0.97 0.015 250)',
    tintaCabecalho: 'oklch(0.48 0.12 250)',
    tinta: 'oklch(0.4 0.08 250)',
    icone: '⏭',
    rotulo: 'Pendente',
  },
} as const;

/**
 * Caixa de mensagem de erro/aviso. Com `onVer` vira BOTÃO (Tab/Enter/Espaço,
 * cursor pointer e um hover discreto no tom da própria caixa); sem ele fica o
 * bloco estático de sempre — a aparência não promete uma ação que não existe.
 *
 * A caixa da PENDÊNCIA (28/09) é sempre estática: pendência não é erro, e a aba
 * Erros não tem registro de skip — clicável, ela prometeria uma navegação que
 * abre vazia.
 */
/**
 * "NÃO CHEGARAM A ESTA ETAPA" — a seção que responde o pedido de 07/10:
 *
 * > *"tem 100 empresas iniciais no agrupamento 1, porém no agrupamento 2
 * > rodaram apenas 90, eu quero que no agrupamento 2 tenha esse aviso
 * > informando as 10 empresas que não processaram e o porquê"*
 *
 * Duas categorias, separadas de propósito:
 *
 *  - **sem log nesta etapa** — não há registro nenhum dela aqui. É o caso que o
 *    dono não conseguia enxergar: a empresa não falhou em lugar nenhum, ela
 *    simplesmente não veio, e por isso nenhuma contagem de erro a mostrava;
 *  - **chegou e não concluiu** — tem log aqui, o trabalho começou e parou.
 *    Diagnóstico diferente e ação diferente.
 *
 * O rótulo da base (`baseLabel`) é exibido: "de 100 empresas do fluxo" na macro,
 * "de 90 que concluíram em BOT_X" no micro. Sem ele o número ficaria solto, e já
 * aconteceu de um indicador sem denominador visível ser lido como outra coisa.
 */
const SecaoNaoChegaram: React.FC<{
  dados: NaoChegaram;
  /**
   * As parcelas do NÓ, quando há. O cabeçalho passa a usar `naoChegou`/`base`
   * daqui, para o popup e a bolha nunca mais divergirem no mesmo `↳`.
   */
  parcelas?: ParcelasDoNo | null;
  // eslint-disable-next-line no-unused-vars
  apelidoDoBot?: (bot: string) => string;
}> = ({ dados, parcelas, apelidoDoBot }) => {
  const [busca, setBusca] = useState('');
  // RECOLHIDA por padrão (09/10, pedido do dono: *"deixe possível recolher a
  // parte de 'não rodaram neste agrupamento' e mostrando apenas o resumo"*).
  // O resumo por nó continua visível — é por ele que se age; a lista nominal
  // e a busca ficam a um clique, para quem precisa do código da empresa.
  const [aberta, setAberta] = useState(false);
  const { itens, porNo, truncado } = dados;

  // O `↳` E A BASE SAEM DAS PARCELAS DO NÓ (09/10) — ver `parcelas` em
  // `PopupDados`. Sem elas, a base publicada em `naoChegaram`.
  const base = parcelas ? parcelas.base : dados.base;
  const naoRodaram = parcelas ? parcelas.naoChegou : itens.length;
  // Quantas estão NOMEADAS abaixo. Diverge de `naoRodaram` quando as rotas
  // discordam do recorte (o universo do departamento tem empresa que não
  // aparece em agrupamento nenhum, logo não tem nome para listar): aí o botão
  // promete o que a lista entrega, em vez de abrir com menos linhas do que diz.
  const nomeadas = itens.length;

  const rotulo = (no: string | null) => {
    if (no === null) return 'nunca entrou no fluxo';
    return apelidoDoBot ? apelidoDoBot(no) : no;
  };

  // A busca filtra por CÓDIGO e por NÓ: com 90 linhas, procurar "a 1085" e
  // "quem parou no Auditor" são as duas perguntas reais, e a segunda não teria
  // resposta filtrando só por código.
  const alvo = normalizarBusca(busca);
  const filtrados = alvo
    ? itens.filter(
        e =>
          normalizarBusca(e.codiEmp).includes(alvo) ||
          normalizarBusca(rotulo(e.parouEm)).includes(alvo),
      )
    : itens;

  return (
    <div
      style={{
        padding: '12px 20px',
        borderBottom: '1px solid oklch(0.93 0.008 160)',
        background: 'oklch(0.985 0.012 85)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: '8px',
          marginBottom: '8px',
        }}
      >
        <span
          style={{
            fontSize: '10.5px',
            fontWeight: 700,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: C.amberFg,
          }}
        >
          Não rodaram neste agrupamento ({naoRodaram}
          {truncado ? '+' : ''})
        </span>
        <span style={{ flex: 'none', fontSize: '11px', color: 'oklch(0.5 0.015 165)' }}>
          de {base} {dados.baseLabel}
        </span>
      </div>

      {/* O RESUMO POR NÓ abre a seção (07/10, pedido do dono: "só agrupar por
          nó"). É por aqui que se age: "68 pararam em Auditor Impostos SN" aponta
          um nó para investigar, enquanto 90 códigos de empresa não apontam nada.
          A lista detalhada continua existindo logo abaixo, para quem precisa do
          nome da empresa. */}
      {/* `porNo` VAZIO não renderiza a lista (09/10): numa área de um nó só
          — Departamento Pessoal — ninguém "parou em" outro agrupamento, porque
          não existe outro. Um `<ul>` vazio deixava um buraco de 10px antes da
          nota, sem nada dentro. */}
      {porNo.length > 0 && (
        <ul
          style={{
            listStyle: 'none',
            margin: '0 0 10px',
            padding: 0,
            display: 'grid',
            gap: '3px',
          }}
        >
          {porNo.map(p => (
            <li
              key={p.no ?? '__sem_no__'}
              style={{ fontSize: '12px', color: 'oklch(0.32 0.03 165)' }}
            >
              <span style={{ fontWeight: 700 }}>{p.quantidade}</span>
              {p.no === null ? ' nunca entraram no fluxo' : ` pararam em ${rotulo(p.no)}`}
            </li>
          ))}
        </ul>
      )}

      {/* A DIFERENÇA EXPLICADA, quando existe (09/10): o `↳` conta sobre o
          universo do departamento e a lista nomeia quem apareceu em algum
          agrupamento. Quando o universo tem empresa que não apareceu em nó
          nenhum, os dois números divergem — e um número sem explicação nesta
          tela é exatamente o que o dono vinha conferindo na calculadora. */}
      {naoRodaram > nomeadas && (
        <p style={{ margin: '0 0 8px', fontSize: '11px', color: 'oklch(0.5 0.015 165)' }}>
          {naoRodaram - nomeadas} {naoRodaram - nomeadas > 1 ? 'não têm' : 'não tem'} registro neste
          agrupamento
          {nomeadas > 0 && ' e não aparecem na lista abaixo'}.
        </p>
      )}

      {/* RECOLHER (09/10): fechada mostra só o resumo por nó, que é por onde se
          age. O detalhe — busca e lista nominal — vem a um clique. */}
      {/* O BOTÃO só existe quando há NOME para listar: com `nomeadas === 0`
          (todas as que não rodaram estão fora dos agrupamentos) ele prometeria
          "ver as 0 empresas" e abriria um painel vazio. A nota acima já disse
          o que houve com elas. */}
      {nomeadas > 0 && (
        <button
          type="button"
          onClick={() => setAberta(a => !a)}
          aria-expanded={aberta}
          style={{
            marginBottom: aberta ? '8px' : 0,
            padding: 0,
            border: 'none',
            background: 'none',
            font: 'inherit',
            fontSize: '11px',
            fontWeight: 600,
            color: C.amberFg,
            cursor: 'pointer',
          }}
        >
          {aberta ? '▾ ocultar as empresas' : `▸ ver as ${nomeadas} empresas`}
        </button>
      )}

      {aberta && (
        <>
          {/* Busca DENTRO da seção (pedido do dono: "deixe possível tb pesquisar
          nessa telinha"). Com 90 linhas numa área rolável, achar uma empresa
          específica sem busca é rolar no olho. */}
          <input
            type="search"
            value={busca}
            onChange={ev => setBusca(ev.target.value)}
            placeholder="buscar empresa ou etapa…"
            aria-label="Buscar nas empresas que não rodaram neste agrupamento"
            style={{
              width: '100%',
              boxSizing: 'border-box',
              padding: '5px 8px',
              marginBottom: '6px',
              fontSize: '11.5px',
              border: '1px solid oklch(0.88 0.03 85)',
              borderRadius: '5px',
              background: C.white,
              color: 'oklch(0.3 0.02 165)',
            }}
          />

          {/* ALTURA FIXA COM ROLAGEM (07/10): sem isto, 90 linhas empurravam as
          pílulas e a lista de empresas para fora da tela — o popup "ficava
          enorme, sem paginação, sem scroll". O resto do popup volta ao lugar e
          a seção rola por dentro. */}
          <div style={{ maxHeight: '168px', overflowY: 'auto', overscrollBehavior: 'contain' }}>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '4px' }}>
              {filtrados.map(e => (
                <li key={e.codiEmp} style={{ fontSize: '11.5px', color: 'oklch(0.4 0.02 165)' }}>
                  <span style={{ fontFamily: FONTE_MONO, fontWeight: 600 }}>{e.codiEmp}</span>
                  {/* "sem log nesta etapa" era jargão (07/10, pedido do dono: "troque
                  por algo mais entendível para alguém não técnico"). O motivo
                  REAL vem junto: não ter log aqui é o sintoma, ter travado num nó
                  anterior é a causa. */}
                  {' · não rodou neste agrupamento'}
                  {e.parouEm === null
                    ? ' — nunca entrou no fluxo'
                    : ` — parou em ${rotulo(e.parouEm)}`}
                </li>
              ))}
            </ul>

            {!filtrados.length && (
              <div style={{ fontSize: '11.5px', opacity: 0.75 }}>
                Nenhuma empresa ou etapa com “{busca}”.
              </div>
            )}
          </div>
        </>
      )}

      {truncado && (
        <div style={{ marginTop: '6px', fontSize: '10.5px', opacity: 0.85 }}>
          Lista parcial — a consulta do servidor foi truncada, então o total pode ser maior.
        </div>
      )}

      {/* As duas categorias saíram do cabeçalho e vêm aqui, em voz corrente: lá
          em cima competiam com a base pelo mesmo espaço e viravam ruído. */}
      <div style={{ marginTop: '6px', fontSize: '10.5px', color: 'oklch(0.5 0.015 165)' }}>
        Quem concluiu está no ✓ e quem falhou no ✕ — aqui ficam só as que não rodaram.
      </div>
    </div>
  );
};

const CaixaMensagem: React.FC<{
  tom: 'erro' | 'aviso' | 'pendente';
  /** Sobrescreve o rótulo do cabeçalho — a macro chama tudo de falha. */
  tituloCaixa?: string;
  mensagem: string;
  onVer?: () => void;
  /** Nome acessível do botão — a seta "ver na auditoria →" é aria-hidden. */
  rotulo?: string;
  /**
   * DE QUAL BOT veio esta mensagem (22/09, pedido do dono). Num grupo com vários
   * bots, "Mensagem de erro" sem origem não diz onde agir — e o nó de grupo é
   * justamente onde a empresa passa por N bots. Exibido como apelido (o nome real
   * vai no `title`), igual ao resto da tela. Ausente no nível nano, onde o bot já
   * está no título do popup.
   */
  bot?: string | null;
  /** Apelido para exibição; cai no nome real quando não há. */
  apelidoBot?: string | null;
  /**
   * HISTÓRICO DENTRO DA CAIXA (28/09, escolha do dono entre três opções): a
   * pendente que falhou antes diz quando e por quê, abaixo do motivo e separada
   * por uma linha fina. `texto` ausente = só a frase, sem os dois pontos.
   */
  historico?: { frase: string; texto: string | null } | null;
}> = ({ tom, mensagem, onVer, rotulo, bot, apelidoBot, tituloCaixa, historico }) => {
  const [hover, setHover] = useState(false);
  const t = TOM_CAIXA[tom];
  const clicavel = Boolean(onVer);
  const conteudo = (
    <>
      <div
        style={{
          padding: '5px 10px',
          background: t.fundoCabecalho,
          borderBottom: `1px solid ${t.borda}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '6px',
          fontSize: '10px',
          fontWeight: 700,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          color: t.tintaCabecalho,
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
          <span>{t.icone}</span>
          {/* `flex: none` no rotulo e `flex: 1` no nome do bot (24/09).
              Medido no DOM: o nome precisava de 138px e tinha 122px — faltavam
              16px, e "Transmissao EFD REINF" chegava cortado em "Transmissao EFD
              R...". Os dois disputavam a mesma faixa sem regra de reparticao, e
              quem cedia era sempre o nome (era ele que tinha o `ellipsis`).

              Agora o rotulo ocupa o que precisa e o nome fica com TODO o resto —
              sem tirar nenhum dos dois da tela. */}
          <span style={{ flex: 'none' }}>{tituloCaixa ?? t.rotulo}</span>
          {bot && (
            <span
              /* O title traz o que NAO esta' na tela. Ate' 24/09 ele dizia "Bot
                 de origem: BOT_EFD_REINF" enquanto o texto visivel ja' era o
                 apelido "Transmissao EFD REINF" — passar o mouse repetia o que
                 se estava lendo, e o nome REAL (o que a auditoria filtra) so'
                 aparecia ali quando NAO havia apelido. Agora: com apelido, o
                 title revela o nome real; sem apelido, nao ha' o que revelar. */
              title={apelidoBot && apelidoBot !== bot ? `Nome real do bot: ${bot}` : undefined}
              style={{
                fontFamily: FONTE_MONO,
                fontWeight: 600,
                textTransform: 'none',
                letterSpacing: 0,
                opacity: 0.85,
                // `flex: 1` + `minWidth: 0`: toma todo o espaco que sobra da
                // faixa em vez de disputar com o rotulo. Sem o `minWidth: 0` o
                // flex item nao encolhe abaixo do conteudo e o ellipsis nunca
                // dispara — o texto vazaria em vez de truncar.
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              · {apelidoBot || bot}
            </span>
          )}
        </span>
        {/* Só decoração: o nome acessível do botão vem do aria-label dele (o
            affordance visual não pode ser a única pista de que isso navega).

            `flex: none` + `nowrap` (25/09): sem eles o "ver na auditoria →"
            quebrava em DUAS linhas e a faixa dobrava de altura, porque o nome do
            bot (com `flex: 1`) tomava o espaço e sobrava largura de uma palavra
            só. Quem cede espaço é o nome do bot, que tem `ellipsis` para isso —
            o atalho é curto, fixo e não pode virar duas linhas. */}
        {clicavel && (
          <span aria-hidden="true" style={{ flex: 'none', whiteSpace: 'nowrap' }}>
            ver na auditoria →
          </span>
        )}
      </div>
      <div
        style={{
          padding: '8px 10px',
          fontSize: '12px',
          color: t.tinta,
          lineHeight: 1.4,
          textAlign: 'left',
        }}
      >
        {mensagem}
        {historico && (
          <div
            style={{
              marginTop: '7px',
              paddingTop: '7px',
              borderTop: `1px solid ${t.borda}`,
              fontSize: '11.5px',
            }}
          >
            <div style={{ fontWeight: 600 }}>{historico.frase}</div>
            {historico.texto && <div>{historico.texto}</div>}
          </div>
        )}
      </div>
    </>
  );
  const estilo: React.CSSProperties = {
    border: `1px solid ${t.borda}`,
    borderRadius: '8px',
    background: hover && clicavel ? t.fundoHover : t.fundo,
    overflow: 'hidden',
  };
  if (!clicavel) return <div style={estilo}>{conteudo}</div>;
  return (
    <button
      type="button"
      onClick={onVer}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      aria-label={rotulo ?? 'Ver na auditoria de erros'}
      title="Ver na auditoria de erros"
      style={{
        ...estilo,
        display: 'block',
        width: '100%',
        padding: 0,
        margin: 0,
        font: 'inherit',
        textAlign: 'left',
        cursor: 'pointer',
        transition: 'background 0.12s',
      }}
    >
      {conteudo}
    </button>
  );
};

interface PopupProcessosProps {
  dados: PopupDados | null;
  onClose: () => void;
  /** Clique na mensagem de erro/aviso → auditoria filtrada. Ausente = caixa inerte. */
  // eslint-disable-next-line no-unused-vars
  onVerNaAuditoria?: (alvo: AlvoAuditoria) => void;
  /**
   * Nome real do bot -> apelido de exibicao (22/09). A caixa de mensagem passou a
   * dizer de qual bot veio o erro/aviso, e a tela inteira exibe apelido, nunca o
   * nome tecnico. Ausente = cai no nome real, sem quebrar.
   */
  // eslint-disable-next-line no-unused-vars
  apelidoDoBot?: (bot: string) => string;
  /**
   * Janela da TELA (ISO), repassada ao histórico da linha para ele falar do MESMO
   * recorte que o nó (decisão do dono, 05/10). Ausente = o histórico não é
   * oferecido: sem período não há como garantir que ele concorda com o número do
   * nó, e uma linha do tempo de outro recorte contradiz a tela que a abriu.
   */
  periodo?: { de?: string; ate?: string };
}

export const PopupProcessos: React.FC<PopupProcessosProps> = ({
  dados,
  onClose,
  onVerNaAuditoria,
  apelidoDoBot,
  periodo,
}) => {
  /**
   * Quais linhas estão com o histórico aberto (chave da empresa + bot).
   *
   * Estado LOCAL e um Set, como o filtro de status: cada abertura do popup
   * congela um `dados` novo, então abrir/fechar/reabrir reseta — e várias linhas
   * podem ficar abertas ao mesmo tempo, que é como se compara duas empresas.
   * Cada `HistoricoDaUnidade` monta com `aberto`, e é ele que dispara (ou não) a
   * consulta sob demanda.
   */
  const [historicoAberto, setHistoricoAberto] = useState<Set<string>>(new Set());
  const alternarHistorico = (chave: string) =>
    setHistoricoAberto(atual => {
      const proximo = new Set(atual);
      if (proximo.has(chave)) proximo.delete(chave);
      else proximo.add(chave);
      return proximo;
    });
  // Filtro LOCAL por status (pedido 08/09) — estado do próprio popup: cada abertura
  // congela um `dados` novo, então abrir/fechar/reabrir sempre reseta a seleção.
  const [statusLocal, setStatusLocal] = useState<StatusEsteira[]>([]);
  /**
   * BUSCA DENTRO DO POPUP (24/09). As listas passaram de dezenas para CENTENAS
   * (o nó "Transmissão dos Impostos LP" sozinho tem 354 empresas), e achar uma
   * empresa específica virava rolagem. Casa por NOME ou por CÓDIGO: quem vem da
   * planilha sabe o código, quem vem do atendimento sabe o nome.
   */
  const [busca, setBusca] = useState('');
  // Popup analítico (13/10): aba (etapa ou RPA) e parcela abertas. Estado local,
  // como o filtro de status — cada abertura congela um `dados` novo e reseta.
  const [abaId, setAbaId] = useState<string | null>(null);
  const [categoria, setCategoria] = useState<CategoriaDoPopup | null>(null);
  /** Grupo do "Top erros" aberto (chave normalizada da mensagem). */
  const [erroSel, setErroSel] = useState<string | null>(null);
  /** Recorte por movimento: só quem faturou, só quem não, ou todos. */
  const [movSel, setMovSel] = useState<'com' | 'sem' | null>(null);
  useEffect(() => {
    setMovSel(dados?.painel?.movimentoInicial ?? null);
    setStatusLocal([]);
    setBusca('');
    setAbaId(null);
    setErroSel(null);
    setCategoria(dados?.painel?.categoriaInicial ?? null);
  }, [dados]);

  if (!dados) return null;
  const pp = dados.painel ?? null;
  const aba = pp ? (pp.abas.find(a => a.id === abaId) ?? pp.abas[0] ?? null) : null;
  // Listas de AUSÊNCIA: quem não tem registro na aba não está em `companies`,
  // então sai das listas do painel e é nomeada pelo mapa de nomes.
  let ausentes: string[] | null = null;
  if (aba && categoria === 'naoConstam') ausentes = aba.painel.naoConstam;
  else if (aba && categoria === 'semMovimento') ausentes = aba.painel.semMovimento;
  else if (aba && categoria === 'naoSeguiram') ausentes = aba.funil?.naoSeguiram ?? [];
  // Listas de DESFECHO: as linhas ricas, recortadas pela parcela aberta. Só quem
  // está no universo — o cartão conta o universo, e a lista tem que bater com ele.
  let listaBase: IEmpresaComStatus[] | null = dados.companies;
  if (aba) {
    if (ausentes) listaBase = null;
    else if (categoria === 'foraDoUniverso') {
      const fora = new Set(aba.painel.foraDoUniverso);
      listaBase = aba.companies.filter(c => fora.has(c.codiEmp));
    } else {
      const noPainel = new Set([...aba.painel.ok, ...aba.painel.erro, ...aba.painel.andamento]);
      listaBase = aba.companies.filter(
        c => noPainel.has(c.codiEmp) && (!categoria || categoriaDoItem(c) === categoria),
      );
    }
  }
  // RECORTE POR MOVIMENTO: vale para as duas listas (desfecho e ausência).
  const doLadoDoMovimento = (codi: string): boolean => {
    if (!movSel || !pp?.comMovimento) return true;
    return pp.comMovimento.has(codi) === (movSel === 'com');
  };
  if (aba && movSel) {
    if (listaBase) listaBase = listaBase.filter(c => doLadoDoMovimento(c.codiEmp));
    if (ausentes) ausentes = ausentes.filter(doLadoDoMovimento);
  }
  // TOP ERROS (13/10): as empresas em erro da aba, agrupadas pela mensagem.
  const emErroNaAba = aba
    ? aba.companies.filter(
        c => aba.painel.erro.includes(c.codiEmp) && categoriaDoItem(c) === 'erro',
      )
    : [];
  const rankingDeErros = topErros(emErroNaAba);
  if (aba && erroSel && categoria === 'erro' && listaBase) {
    listaBase = listaBase.filter(c => chaveDoErro(mensagemDoErro(c)) === erroSel);
  }
  /** codi → registro de "pendente" da aba, para a lista de ausência dizer o motivo. */
  const pendentePorCodi = new Map<string, IEmpresaComStatus>();
  if (aba) {
    const pendentes = new Set(aba.painel.pendentes);
    aba.companies.forEach(c => {
      if (pendentes.has(c.codiEmp)) pendentePorCodi.set(c.codiEmp, c);
    });
  }
  // A lista do popup já chega com o filtro GLOBAL aplicado (congelado no clique);
  // o filtro local só REFINA — as pílulas (totais) descrevem a lista congelada.
  // `filtrarPorStatusEfetivo`, nao `filtrarPorStatus`: as pilulas contam pela
  // regua do no' (com a ocorrencia de outro bot), e filtrar por outra regua
  // abriria o popup VAZIO — a pilula diria "1 falha" e nenhuma linha casaria.
  const porStatus = listaBase ? filtrarPorStatusEfetivo(listaBase, statusLocal) : null;
  // A busca refina DEPOIS do status, e as pílulas seguem contando a lista
  // congelada (`dados.companies`) — elas descrevem o total, não o que a busca
  // deixou na tela. É a mesma escolha do filtro local, e o comentário de
  // `pilulasPopup` explica por quê.
  const termoBusca = normalizarBusca(busca);
  const visiveis =
    porStatus && termoBusca
      ? porStatus.filter(
          c =>
            normalizarBusca(c.nome).includes(termoBusca) ||
            normalizarBusca(c.codiEmp).includes(termoBusca),
        )
      : porStatus;
  const companies = visiveis
    ? // A LINHA SEGUE A REGUA DO NO' (28/09, decisao do dono).
      //
      // A empresa que concluiu no bot-b e esta' falhando AGORA no bot-a chega
      // aqui como `done` com `ocorrenciaEmOutroBot: 'err'` — a dedup faz `done`
      // vencer. O no' e as pilulas ja' a contavam como FALHA desde 22-24/09
      // (`statusEfetivo`: "se a empresa teve falha em qualquer etapa, nao pode
      // alocar como sucesso"); so' a LINHA seguia o `st` cru, e o resultado era
      // o badge "Concluido" com caixa de erro embaixo.
      //
      // Medido em producao: 93 empresas, 89 delas em "Transmissao dos Impostos
      // LP". Agora o badge, o tom e a ORDEM da linha saem de `statusEfetivo` —
      // "Concluido" passa a significar concluido em TODOS os bots do grupo, e
      // por isso nunca mais aparece com mensagem de erro.
      //
      // A ordem tambem muda de proposito: pela `st` crua essas linhas iam para
      // o fim da lista, junto das concluidas, que e' onde ninguem procura falha.
      ordenarParaExibicao(visiveis.map(c => ({ c, st: statusEfetivo(c) }))).map(
        ({ c, st: stLinha }) => {
          const meta = ST_META[stLinha] ?? ST_META.wait;
          const t = TONES[TONE_DO_STATUS[stLinha] ?? 'idle'];
          const temMotivo = Boolean(c.motivo) && (c.st === 'err' || c.st === 'warn');
          // O numero do badge conta FALHAS, nao bots (24/09).
          //
          // Ate' aqui ele mostrava `qtdBots` — "em quantos bots do grupo a empresa
          // aparece" —, e "Falha (4)" era lido como "falhou 4 vezes". Sao grandezas
          // diferentes e o proprio dado denunciava: a empresa 1083 tinha badge "(4)"
          // com 2 falhas no banco, e uma empresa com 1 falha aparecia sem numero
          // nenhum (porque estava em 1 bot so').
          //
          // So' aparece onde FAZ SENTIDO — em `err` — e so' quando ha' mais de uma:
          // "Falha (1)" nao diz nada que "Falha" ja' nao diga. Empresa concluida que
          // falhou antes tem o selo proprio (`falhasAnteriores`), com outro texto.
          const falhas = c.falhas ?? 0;
          // `c.st` CRU aqui, de proposito: `falhas` acompanha o bot VENCEDOR da
          // dedup. Na linha promovida a falha (concluiu num bot, falha em outro)
          // o numero seria a contagem do bot que CONCLUIU colada no badge do bot
          // que falhou.
          const badge = c.st === 'err' && falhas > 1 ? `${meta.label} (${falhas})` : meta.label;
          // CAIXA DA PENDENTE (28/09, pedido do dono: "da mesma forma que os
          // erros, melhora essa visualização para as pendentes").
          //
          // Pelo status EFETIVO, e lendo SÓ `motivoSkip` — nunca `motivo` nem
          // `motivoErro`. Medido em produção: 1276 pares têm skip como último
          // desfecho, 100% com motivo gravado, e 251 deles chegam com
          // `motivo_erro` preenchido por uma falha ANTIGA. Ler o campo errado
          // mostraria "DCTFWeb v2: nova aba não chegou..." como razão de uma
          // pendência cujo motivo real é "1 sem tributos".
          const motivoSkip = stLinha === 'skip' && c.motivoSkip ? c.motivoSkip : null;
          // Data lida literalmente; formato inesperado devolve null e a linha se
          // comporta como a de backend antigo (selo sem data, caixa sem histórico).
          const diaUltimaFalha = diaDaFalha(c.ultimaFalhaEm);
          // O histórico só entra na caixa quando há caixa, contagem E data.
          const historicoNaCaixa =
            motivoSkip && falhas > 0 && diaUltimaFalha
              ? {
                  frase: `Antes disso falhou ${falhas}x (${diaUltimaFalha})${
                    c.ultimaFalhaMotivo ? ':' : ''
                  }`,
                  texto: c.ultimaFalhaMotivo ?? null,
                }
              : null;
          return {
            key: c.codiEmp,
            name: c.nome,
            badge,
            badgeBg: t.bg,
            badgeFg: t.fg,
            badgeDot: t.dot,
            accent: t.dot,
            temMotivo,
            motivo: c.motivo ?? '',
            // As DUAS mensagens, para a macro poder mostrar erro E alerta.
            motivoErro: c.motivoErro ?? null,
            motivoAviso: c.motivoAviso ?? null,
            isErro: c.st === 'err',
            // SELO "ja' falhou Nx" (24/09): a empresa esta' CONCLUIDA hoje, mas
            // falhou antes na janela. Ate' 23/09 ela aparecia como erro — o status
            // vinha de `errors > 0`, que e' cumulativo, entao uma falha antiga a
            // mantinha em vermelho para sempre (474 pares em producao, 63%).
            //
            // Corrigir o status sozinho esconderia que houve problema; o selo e' o
            // que preserva a informacao sem poluir a contagem de erros. So' aparece
            // quando o estado ATUAL nao e' falha — em `err` o numero ja' esta' no
            // badge e repetir seria ruido.
            // "passou em 8 · falhou em 1" (25/09). A linha mostra o status VENCEDOR
            // da dedup, entao a empresa que concluiu em 8 bots e falhou em 1
            // aparecia como "Concluído" com caixa vermelha embaixo — sem dizer de
            // onde vinha a contradicao. So' aparece quando ha' os DOIS: com um
            // lado so' o badge ja' conta a historia inteira.
            resumoBots:
              (c.botsOk ?? 0) > 0 && (c.botsFalha ?? 0) > 0
                ? `passou em ${c.botsOk} · falhou em ${c.botsFalha}`
                : null,
            //
            // `!historicoNaCaixa` (28/09): quando a caixa da pendente ja' conta o
            // historico, o selo NAO e' renderizado — o mesmo fato em dois lugares
            // e' como a tela comeca a se contradizer.
            falhasAnteriores:
              stLinha !== 'err' && (c.falhas ?? 0) > 0 && !historicoNaCaixa
                ? (c.falhas ?? 0)
                : null,
            // So' a DATA vai para o selo. O TEXTO da falha antiga fica restrito a
            // caixa da pendente: "se deu status como concluido apenas apresente o
            // status, sem as mensagens de erro" (dono, 25/09 e 28/09).
            ultimaFalhaEm: c.ultimaFalhaEm ?? null,
            diaUltimaFalha,
            motivoSkip,
            historicoNaCaixa,
            // O selo "ja' falhou Nx" aparece em QUALQUER estado que nao seja `err`
            // — inclusive `skip` (Pendente), que em producao e' o caso MAIS COMUM:
            // 256 pares (empresa, bot) contra 115 concluidos (medido 25/09).
            //
            // O tooltip precisa do estado para nao MENTIR: ate' 25/09 ele dizia
            // "Esta empresa concluiu, mas N falharam antes" mesmo na linha marcada
            // como Pendente, que foi o que o dono estranhou. O selo em si esta'
            // certo e e' util — "pendente agora, mas ja' falhou 3x" e' um sinal
            // diferente de uma pendente limpa; quem estava errado era o texto.
            estadoDoSelo: stLinha,
            foraDoResumo: Boolean(c.foraDoResumo),
            // O bot do PRÓPRIO item vence o do contexto: em lista de grupo cada
            // empresa pode ter travado num bot diferente.
            bot: c.bot ?? dados.botPadrao ?? null,
            ts: c.ts ?? null,
            // Erro/aviso em OUTRO bot do grupo que a dedup não elegeu (ex.: concluiu
            // no bot-b, falhou no bot-a e `done` venceu): o texto/estado da linha
            // segue o vencedor, só o ATALHO para a auditoria continua disponível.
            ocorrenciaEmOutroBot: c.ocorrenciaEmOutroBot ?? null,
            // Mensagem daquela ocorrência (12/09): antes só o atalho sobrevivia à
            // dedup e a linha ficava muda — o usuário via "Concluído" sem nenhum
            // sinal do alerta/erro registrado no outro bot do grupo.
            motivoEmOutroBot: c.motivoEmOutroBot ?? null,
            // E de QUAL bot ela veio (22/09) — antes o nome era descartado na dedup,
            // e a caixa dizia "Mensagem de erro" sem identificar a origem.
            botDaOcorrencia: c.botDaOcorrencia ?? null,
          };
        },
      )
    : null;
  const summary = pp ? null : pilulasDoPopup(dados.companies, dados.macro);
  const ausentesVisiveis = ausentes
    ? ausentes
        .map(codi => ({ codi, nome: pp?.nomes.get(codi) ?? codi }))
        .filter(
          e =>
            !termoBusca ||
            normalizarBusca(e.nome).includes(termoBusca) ||
            normalizarBusca(e.codi).includes(termoBusca),
        )
    : null;
  const tamanhoDaLista = ausentes ? ausentes.length : (listaBase?.length ?? 0);
  const erroParcialSet = new Set(pp?.erroParcial ?? []);

  /** Os cartões de parcela da aba aberta — número, percentual e clique. */
  const cartoes = aba
    ? [
        {
          chave: null as CategoriaDoPopup | null,
          rotulo: 'Total do regime',
          valor: aba.painel.base,
          pct: '',
          cor: 'oklch(0.3 0.03 165)',
          dica: 'Empresas ativas do regime com contrato vigente. Clique para ver todas as que têm registro aqui.',
        },
        {
          chave: 'ok' as CategoriaDoPopup,
          rotulo: 'Concluídas',
          valor: aba.painel.ok.length,
          pct: textoPct(aba.painel.ok.length, aba.painel.base),
          cor: C.greenFg,
          dica: 'Último resultado foi concluído',
        },
        {
          chave: 'erro' as CategoriaDoPopup,
          rotulo: 'Erro ou alerta',
          valor: aba.painel.erro.length,
          pct: textoPct(aba.painel.erro.length, aba.painel.base),
          cor: C.redFg,
          dica: 'Último resultado foi erro ou alerta. Quem errou e depois passou conta como concluída.',
        },
        ...(aba.painel.andamento.length
          ? [
              {
                chave: 'andamento' as CategoriaDoPopup,
                rotulo: 'Em andamento',
                valor: aba.painel.andamento.length,
                pct: textoPct(aba.painel.andamento.length, aba.painel.base),
                cor: C.blueFg,
                dica: 'Rodando agora ou na fila',
              },
            ]
          : []),
        {
          chave: 'naoConstam' as CategoriaDoPopup,
          rotulo: 'Não constam no fluxo',
          valor: aba.painel.naoConstam.length,
          pct: textoPct(aba.painel.naoConstam.length, aba.painel.base),
          cor: C.amberFg,
          dica:
            'Do regime, com movimento, que ainda não concluíram nem erraram aqui: sem registro, ' +
            `ou só com log de "pendente"${aba.painel.pendentes.length ? ` (${aba.painel.pendentes.length} com pendente)` : ''}`,
        },
        ...(aba.painel.movimentoConhecido
          ? [
              {
                chave: 'semMovimento' as CategoriaDoPopup,
                rotulo: 'Sem movimento e sem registro',
                valor: aba.painel.semMovimento.length,
                pct: textoPct(aba.painel.semMovimento.length, aba.painel.base),
                cor: 'oklch(0.48 0.015 165)',
                dica: `Sem nota de saída ou serviço em ${pp?.competencia} e sem registro aqui`,
              },
            ]
          : []),
        ...(aba.funil
          ? [
              {
                chave: 'naoSeguiram' as CategoriaDoPopup,
                rotulo: `Não seguiram de ${aba.funil.anterior}`,
                valor: aba.funil.naoSeguiram.length,
                pct: `${aba.funil.seguiram.length} de ${aba.funil.okAnterior} seguiram`,
                cor: aba.funil.naoSeguiram.length ? C.amberFg : C.greenFg,
                dica: `Das ${aba.funil.okAnterior} que concluíram em ${aba.funil.anterior}, quem ainda não tem registro aqui`,
              },
            ]
          : []),
        ...(aba.painel.foraDoUniverso.length
          ? [
              {
                chave: 'foraDoUniverso' as CategoriaDoPopup,
                rotulo: 'Rodaram fora do total',
                valor: aba.painel.foraDoUniverso.length,
                pct: '',
                cor: 'oklch(0.48 0.015 165)',
                dica: 'Têm registro aqui mas não estão no total: contrato encerrado, inativas ou de outro regime',
              },
            ]
          : []),
      ]
    : [];

  /** Handler da caixa clicável — só existe quando o pai sabe navegar. */
  const aoVerNaAuditoria = (
    pc: { key: string; bot: string | null; ts: string | null },
    erro: boolean,
  ) => {
    if (!onVerNaAuditoria) return;
    onClose();
    onVerNaAuditoria({
      codiEmp: pc.key,
      bot: pc.bot,
      status: erro ? 'erro' : 'warning',
      ts: pc.ts,
    });
  };

  /**
   * Atalho da ocorrência que a dedup NÃO elegeu: vai SEM bot de propósito — a
   * ocorrência está em outro bot do grupo e filtrar pelo vencedor a esconderia
   * (é o caso que `erroDoPopup` documenta como "popup de grupo não inventa bot").
   */
  const aoVerOcorrenciaDeOutroBot = (pc: {
    key: string;
    ts: string | null;
    ocorrenciaEmOutroBot: 'err' | 'warn' | null;
  }) => {
    if (!onVerNaAuditoria || !pc.ocorrenciaEmOutroBot) return;
    onClose();
    onVerNaAuditoria({
      codiEmp: pc.key,
      bot: null,
      status: pc.ocorrenciaEmOutroBot === 'err' ? 'erro' : 'warning',
      ts: pc.ts,
    });
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'oklch(0.2 0.02 165 / 0.42)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 60,
        padding: 'clamp(10px, 2vw, 28px)',
      }}
    >
      <div
        data-tour={pp ? 'popup' : undefined}
        onClick={ev => ev.stopPropagation()}
        style={{
          background: pp
            ? 'linear-gradient(160deg, rgba(255,255,255,0.97), rgba(246,251,249,0.94))'
            : 'oklch(1 0 0)',
          border: pp ? '1px solid rgba(255,255,255,0.9)' : undefined,
          borderRadius: pp ? '22px' : '14px',
          width: '100%',
          // 420px cortava a razao social no meio ("PIRRONCELLI CONSULTORIA EM
          // PUBLICID...") com espaco de sobra na tela — o popup lista empresas,
          // e o nome e' a informacao que se vem buscar nele. 560px cabe as
          // razoes sociais tipicas ao lado das pilulas de status.
          //
          // `width: 100%` + `maxWidth` mantem o responsivo: em tela estreita o
          // popup continua acompanhando a largura disponivel (o pai ja' tem
          // `padding: 20px`), entao o numero maior nao cria rolagem horizontal.
          //
          // POPUP ANALÍTICO (13/10, pedido do dono: "o modal... seja maior, está
          // muito pequeno"): ocupa quase a tela, porque passa a ter abas por RPA,
          // os cartões de parcela e a lista em colunas. A altura é FIXA nesse
          // modo — trocar de aba ou de parcela não pode fazer o modal pular de
          // tamanho debaixo do cursor.
          maxWidth: pp ? 'min(1500px, 96vw)' : '560px',
          maxHeight: pp ? '94vh' : '80vh',
          ...(pp ? { height: '94vh' } : {}),
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: pp
            ? '0 30px 90px oklch(0.2 0.02 165 / 0.35), inset 0 1px 0 rgba(255,255,255,0.9)'
            : '0 20px 60px oklch(0.2 0.02 165 / 0.28)',
        }}
      >
        <div
          style={{
            padding: '18px 20px',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: '12px',
            borderBottom: '1px solid oklch(0.93 0.008 160)',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', minWidth: 0 }}>
            <div title={dados.titleTooltip} style={{ fontSize: '15.5px', fontWeight: 600 }}>
              {dados.title}
            </div>
            <div style={{ fontSize: '12px', color: 'oklch(0.55 0.015 165)' }}>{dados.sub}</div>
          </div>
          <div
            onClick={onClose}
            style={{
              flex: 'none',
              width: '26px',
              height: '26px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'oklch(0.96 0.006 160)',
              fontSize: '13px',
              cursor: 'pointer',
              color: 'oklch(0.5 0.015 165)',
            }}
          >
            ✕
          </div>
        </div>

        {/* NÃO PASSARAM DA ETAPA (07/10, pedido do dono) — seção própria, acima
            das pílulas de status.

            Por que seção e não pílula: as pílulas filtram a lista por STATUS
            canônico (`filtrarPorStatusEfetivo`), e "não chegou" não é um status
            — é a AUSÊNCIA de registro, que nenhum status descreve. Inventar uma chave
            nova ali abriria o popup vazio (a lição registrada em
            `pilulasPopup.ts`). Aqui a lista é própria e não disputa régua com
            nada.

            Separada em SEM LOG × NÃO CONCLUIU porque são diagnósticos diferentes
            e levam a ações diferentes. */}
        {/* O PORTÃO SEGUE O NÚMERO QUE A SEÇÃO EXIBE (09/10).
            Era `itens.length > 0`, e com o `↳` vindo das parcelas isso abria um
            buraco: `itens` é a união dos agrupamentos (344) e `naoChegou` é
            sobre o universo (345), então um nó em que TODAS as 344 rodaram tem
            `itens` vazio e `naoChegou` 1 — a seção sumia enquanto a bolha ao
            lado exibia `↳ 1`. A contradição voltava por omissão, que é a mais
            difícil de notar. */}
        {pp && aba && (
          <div
            style={{
              padding: '16px 22px 18px',
              display: 'flex',
              flexDirection: 'column',
              gap: '14px',
              borderBottom: '1px solid oklch(0.93 0.008 160)',
              background:
                'radial-gradient(700px 220px at 0% 0%, oklch(0.93 0.07 165 / 0.55), transparent 65%), ' +
                'radial-gradient(700px 220px at 100% 0%, oklch(0.93 0.06 235 / 0.45), transparent 65%)',
            }}
          >
            {/* ABAS POR RPA — só quando a etapa tem mais de um. */}
            {pp.abas.length > 1 && (
              <div
                role="tablist"
                data-tour="popup-abas"
                style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}
              >
                {pp.abas.map(a => {
                  const ativa = a.id === aba.id;
                  return (
                    <button
                      key={a.id}
                      type="button"
                      role="tab"
                      aria-selected={ativa}
                      title={a.titleTooltip}
                      onClick={() => {
                        setAbaId(a.id);
                        // A parcela aberta pode não existir na outra aba (o funil
                        // é só da etapa): volta para "todas".
                        if (categoria === 'naoSeguiram' && !a.funil) setCategoria(null);
                      }}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '8px 14px',
                        borderRadius: '12px',
                        border: `1px solid ${ativa ? 'oklch(0.22 0.026 168)' : 'rgba(255,255,255,0.9)'}`,
                        background: ativa ? 'oklch(0.22 0.026 168)' : 'rgba(255,255,255,0.7)',
                        color: ativa ? 'oklch(0.96 0.01 160)' : 'oklch(0.3 0.02 165)',
                        font: 'inherit',
                        fontSize: '12.5px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        boxShadow: '0 2px 8px rgba(16, 60, 48, 0.08)',
                      }}
                    >
                      {a.rotulo}
                      <span style={{ fontFamily: FONTE_MONO, fontSize: '11px', opacity: 0.85 }}>
                        ✓{a.painel.ok.length} ✕{a.painel.erro.length} ↳{a.painel.naoConstam.length}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
            {/* CARTÕES DE PARCELA — somam o total do regime; clicar abre a lista. */}
            <div
              data-tour="popup-cartoes"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: '10px',
              }}
            >
              {cartoes.map(ct => {
                const ativo = categoria === ct.chave;
                return (
                  <button
                    key={ct.chave ?? 'total'}
                    type="button"
                    title={ct.dica}
                    aria-pressed={ativo}
                    onClick={() => setCategoria(ativo ? null : ct.chave)}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: '2px',
                      padding: '11px 14px',
                      borderRadius: '14px',
                      border: `1.5px solid ${ativo ? ct.cor : 'rgba(255,255,255,0.95)'}`,
                      background: ativo ? 'rgba(255,255,255,0.98)' : 'rgba(255,255,255,0.72)',
                      boxShadow: ativo
                        ? '0 6px 18px rgba(16, 60, 48, 0.14)'
                        : '0 2px 8px rgba(16, 60, 48, 0.06)',
                      font: 'inherit',
                      textAlign: 'left',
                      cursor: 'pointer',
                    }}
                  >
                    <span
                      style={{ fontSize: '11px', fontWeight: 600, color: 'oklch(0.45 0.02 165)' }}
                    >
                      {ct.rotulo}
                    </span>
                    <span style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                      <span
                        style={{
                          fontSize: '24px',
                          fontWeight: 700,
                          letterSpacing: '-0.02em',
                          color: ct.cor,
                        }}
                      >
                        {ct.valor}
                      </span>
                      <span
                        style={{
                          fontFamily: FONTE_MONO,
                          fontSize: '11.5px',
                          color: 'oklch(0.5 0.015 165)',
                        }}
                      >
                        {ct.pct}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
            {/* COM × SEM MOVIMENTO (13/10): os dois grupos INTEIROS, cada um com o
                seu ok e erro. Clicar recorta a lista para aquele grupo. */}
            {aba.painel.porMovimento && (
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                {(
                  [
                    ['com', 'Com movimento', aba.painel.porMovimento.com],
                    ['sem', 'Sem movimento', aba.painel.porMovimento.sem],
                  ] as const
                ).map(([lado, nome, r]) => {
                  const ativo = movSel === lado;
                  return (
                    <div
                      key={lado}
                      title={`${nome} = ${lado === 'com' ? 'tem' : 'não tem'} nota de saída ou serviço em ${pp.competencia}.`}
                      style={{
                        flex: '1 1 380px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        flexWrap: 'wrap',
                        padding: '8px 10px',
                        borderRadius: '14px',
                        border: `1.5px solid ${ativo ? 'oklch(0.22 0.026 168)' : 'rgba(255,255,255,0.95)'}`,
                        background: ativo ? 'rgba(255,255,255,0.98)' : 'rgba(255,255,255,0.72)',
                        fontSize: '12.5px',
                      }}
                    >
                      {/* Cada fatia é um botão: grupo + parcela de uma vez. A terceira
                          ("não passaram") é a que o dono pediu para poder atacar —
                          sem erro e sem conclusão. */}
                      {(
                        [
                          [
                            null,
                            `${nome}: ${r.total} (${textoPct(r.total, aba.painel.base)} do regime)`,
                            // Mesmas cores do cartão (dono, 14/10): com azul, sem vermelho.
                            lado === 'com' ? C.blueFg : C.redFg,
                            'Ver todas deste grupo que têm registro aqui',
                          ],
                          [
                            'ok',
                            `✓ ${r.ok} concluídas (${textoPct(r.ok, r.total)})`,
                            C.greenFg,
                            'Ver as concluídas deste grupo',
                          ],
                          [
                            'erro',
                            `✕ ${r.erro} com erro (${textoPct(r.erro, r.total)})`,
                            C.redFg,
                            'Ver as com erro ou alerta deste grupo',
                          ],
                          [
                            lado === 'com' ? 'naoConstam' : 'semMovimento',
                            `↳ ${r.ausentes} não passaram (${textoPct(r.ausentes, r.total)})`,
                            C.amberFg,
                            'Sem erro e sem conclusão nesta etapa — as que faltam passar',
                          ],
                        ] as const
                      ).map(([cat, texto, cor, dica]) => {
                        const fatiaAtiva = ativo && categoria === cat;
                        return (
                          <button
                            key={texto}
                            type="button"
                            title={dica}
                            aria-pressed={fatiaAtiva}
                            onClick={() => {
                              if (fatiaAtiva) {
                                setMovSel(null);
                                setCategoria(null);
                              } else {
                                setMovSel(lado);
                                setCategoria(cat);
                              }
                              setErroSel(null);
                            }}
                            style={{
                              padding: '5px 10px',
                              borderRadius: '10px',
                              border: `1px solid ${fatiaAtiva ? cor : 'transparent'}`,
                              background: fatiaAtiva ? 'rgba(255,255,255,1)' : 'transparent',
                              font: 'inherit',
                              fontFamily: cat ? FONTE_MONO : undefined,
                              fontWeight: cat ? 500 : 700,
                              color: cor,
                              cursor: 'pointer',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {texto}
                          </button>
                        );
                      })}
                      {r.andamento > 0 && (
                        <span style={{ fontFamily: FONTE_MONO, color: C.blueFg }}>
                          ⏳ {r.andamento} em andamento
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            <div style={{ fontSize: '11.5px', color: 'oklch(0.48 0.015 165)', textWrap: 'pretty' }}>
              Percentuais sobre o total do regime ({aba.painel.base}). Movimento = nota de saída ou
              serviço em {pp.competencia || '—'}
              {aba.painel.movimentoConhecido ? '' : ' (ainda carregando)'}.
              {aba.id === '__etapa' && categoria === 'erro' && erroParcialSet.size > 0 && (
                <b>
                  {' '}
                  Das {aba.painel.erro.length} em erro, {erroParcialSet.size} concluíram em parte
                  dos RPAs desta etapa (erro parcial) e{' '}
                  {aba.painel.erro.length - erroParcialSet.size} não concluíram em nenhum.
                </b>
              )}
            </div>
          </div>
        )}

        {!pp &&
          dados.naoChegaram &&
          (dados.parcelas ? dados.parcelas.naoChegou : dados.naoChegaram.itens.length) > 0 && (
            <SecaoNaoChegaram
              dados={dados.naoChegaram}
              parcelas={dados.parcelas}
              apelidoDoBot={apelidoDoBot}
            />
          )}

        {summary && (
          <div
            style={{
              padding: '12px 20px',
              display: 'flex',
              gap: '8px',
              flexWrap: 'wrap',
              borderBottom: '1px solid oklch(0.93 0.008 160)',
            }}
          >
            {summary.map(sm => {
              const t = TONES[TONE_DO_STATUS[sm.st] ?? 'idle'];
              const ativo = statusLocal.includes(sm.st);
              // O dot de "Na fila" (idle) é claro demais para texto branco — como
              // no FiltroStatus, a pílula ativa desse tom usa tinta escura.
              const corAtiva = sm.st === 'wait' ? 'oklch(0.3 0.02 165)' : 'oklch(1 0 0)';
              return (
                <div
                  key={sm.st}
                  onClick={() => setStatusLocal(atual => alternarStatusNaLista(atual, sm.st))}
                  title={
                    ativo
                      ? `Deixar de filtrar por ${ST_META[sm.st].label} nesta lista`
                      : `Mostrar só ${ST_META[sm.st].label} nesta lista`
                  }
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '4px 10px',
                    borderRadius: '20px',
                    background: ativo ? t.dot : t.bg,
                    color: ativo ? corAtiva : t.fg,
                    boxShadow: ativo ? `0 0 0 1px ${t.dot}` : 'none',
                    fontSize: '12px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                >
                  <span>{sm.icon}</span>
                  {sm.label}
                </div>
              );
            })}
          </div>
        )}

        {/* CORPO em duas colunas no popup analítico (13/10): "Top erros" à
            esquerda, a lista à direita. `minHeight: 0` nos dois níveis é o que
            deixa a lista rolar dentro do modal em vez de estourá-lo. */}
        <div style={{ display: 'flex', minHeight: 0, flex: pp ? 1 : '0 1 auto' }}>
          {pp && aba && rankingDeErros.length > 0 && (
            <aside
              data-tour="popup-top-erros"
              style={{
                flex: 'none',
                width: 'clamp(250px, 24vw, 380px)',
                overflowY: 'auto',
                padding: '14px 14px 16px 22px',
                borderRight: '1px solid oklch(0.93 0.008 160)',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
              }}
            >
              <div style={{ fontSize: '12.5px', fontWeight: 700, color: 'oklch(0.3 0.03 165)' }}>
                Top erros
                <span style={{ fontWeight: 500, color: 'oklch(0.55 0.015 165)' }}>
                  {' '}
                  · {aba.painel.erro.length} empresas
                </span>
              </div>
              {rankingDeErros.map(g => {
                const ativo = categoria === 'erro' && erroSel === g.chave;
                const largura = aba.painel.erro.length
                  ? Math.max(4, Math.round((g.quantidade / aba.painel.erro.length) * 100))
                  : 0;
                return (
                  <button
                    key={g.chave}
                    type="button"
                    title={g.texto}
                    aria-pressed={ativo}
                    onClick={() => {
                      setCategoria('erro');
                      setErroSel(ativo ? null : g.chave);
                    }}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '5px',
                      padding: '9px 11px',
                      borderRadius: '12px',
                      border: `1.5px solid ${ativo ? C.redFg : 'oklch(0.93 0.008 160)'}`,
                      background: ativo ? TOM_CAIXA.erro.fundo : 'rgba(255,255,255,0.8)',
                      font: 'inherit',
                      textAlign: 'left',
                      cursor: 'pointer',
                    }}
                  >
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'baseline',
                        justifyContent: 'space-between',
                        gap: '8px',
                      }}
                    >
                      <span style={{ fontSize: '17px', fontWeight: 700, color: C.redFg }}>
                        {g.quantidade}
                      </span>
                      <span
                        style={{
                          fontFamily: FONTE_MONO,
                          fontSize: '11px',
                          color: 'oklch(0.5 0.015 165)',
                        }}
                      >
                        {textoPct(g.quantidade, aba.painel.erro.length)} dos erros
                      </span>
                    </span>
                    <span
                      style={{
                        height: '4px',
                        borderRadius: '3px',
                        background: 'oklch(0.94 0.02 28)',
                        overflow: 'hidden',
                      }}
                    >
                      <span
                        style={{
                          display: 'block',
                          width: `${largura}%`,
                          height: '4px',
                          background: 'oklch(0.58 0.18 28)',
                        }}
                      />
                    </span>
                    <span
                      style={{
                        fontSize: '11.5px',
                        lineHeight: 1.35,
                        color: TOM_CAIXA.erro.tinta,
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {g.texto}
                    </span>
                  </button>
                );
              })}
              <div style={{ fontSize: '10.5px', color: 'oklch(0.58 0.015 165)' }}>
                Agrupado pela mensagem (números ignorados). Clique para ver só aquelas empresas.
              </div>
            </aside>
          )}
          <div
            data-tour={pp ? 'popup-lista' : undefined}
            style={{
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* BUSCA (24/09): as listas passaram de dezenas para CENTENAS — o nó
            "Transmissão dos Impostos LP" sozinho tem 354 empresas —, e achar uma
            específica virava rolagem. Só aparece quando há o que buscar: num
            popup de 3 linhas o campo seria ruído. */}
            {(pp ? tamanhoDaLista > 8 : Boolean(dados.companies && dados.companies.length > 8)) && (
              <div
                style={{
                  padding: '10px 20px',
                  borderBottom: '1px solid oklch(0.93 0.008 160)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <input
                  value={busca}
                  onChange={e => setBusca(e.target.value)}
                  placeholder="Buscar empresa por nome ou código"
                  aria-label="Buscar empresa nesta lista"
                  style={{
                    flex: 1,
                    minWidth: 0,
                    padding: '7px 11px',
                    borderRadius: '8px',
                    border: '1px solid oklch(0.9 0.008 160)',
                    fontSize: '12.5px',
                    fontFamily: FONTE_TEXTO,
                    color: 'oklch(0.3 0.02 165)',
                    outline: 'none',
                  }}
                />
                {busca && (
                  <button
                    type="button"
                    onClick={() => setBusca('')}
                    title="Limpar busca"
                    style={{
                      flex: 'none',
                      width: '26px',
                      height: '26px',
                      borderRadius: '8px',
                      border: '1px solid oklch(0.91 0.006 160)',
                      background: 'oklch(1 0 0)',
                      cursor: 'pointer',
                      color: 'oklch(0.5 0.015 165)',
                      fontSize: '12px',
                    }}
                  >
                    ✕
                  </button>
                )}
              </div>
            )}

            {companies && companies.length === 0 && (
              <div
                style={{
                  padding: '22px 20px',
                  textAlign: 'center',
                  fontSize: '12.5px',
                  color: 'oklch(0.55 0.015 165)',
                  textWrap: 'pretty',
                }}
              >
                {pp ? 'Nenhuma empresa nesta parcela.' : 'Nenhuma empresa neste status.'}
              </div>
            )}
            {/* LISTA DE AUSÊNCIA (13/10): não constam · sem movimento · não seguiram.
            Linhas simples — quem não tem registro aqui não tem mensagem nem
            histórico para mostrar; o que interessa é QUEM são. */}
            {ausentesVisiveis && ausentesVisiveis.length === 0 && (
              <div
                style={{
                  padding: '30px 20px',
                  textAlign: 'center',
                  fontSize: '13px',
                  color: 'oklch(0.55 0.015 165)',
                }}
              >
                {ausentes && ausentes.length
                  ? 'Nenhuma empresa casa com a busca.'
                  : 'Nenhuma empresa nesta parcela.'}
              </div>
            )}
            {ausentesVisiveis && ausentesVisiveis.length > 0 && (
              <div
                style={{
                  overflowY: 'auto',
                  flex: 1,
                  minHeight: 0,
                  padding: '12px 22px 18px',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                  gap: '8px 12px',
                  alignContent: 'start',
                }}
              >
                {ausentesVisiveis.map(e => {
                  const semMov = Boolean(pp?.comMovimento) && !pp?.comMovimento?.has(e.codi);
                  const pendente = pendentePorCodi.get(e.codi);
                  return (
                    <div
                      key={e.codi}
                      title={pendente?.motivoSkip}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '10px',
                        padding: '9px 12px',
                        borderRadius: '11px',
                        background: 'rgba(255,255,255,0.85)',
                        border: '1px solid oklch(0.93 0.008 160)',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '3px',
                          minWidth: 0,
                        }}
                      >
                        <NomeEmpresa
                          variante="bloco"
                          nome={e.nome}
                          codigo={e.codi}
                          style={{ fontSize: '12.5px', fontWeight: 600, minWidth: 0 }}
                        />
                        {pendente?.motivoSkip && (
                          <span
                            style={{
                              fontSize: '11px',
                              color: TOM_CAIXA.pendente.tinta,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            ⏭ {pendente.motivoSkip}
                          </span>
                        )}
                      </div>
                      {pendente && (
                        <span
                          style={{
                            flex: 'none',
                            padding: '2px 8px',
                            borderRadius: '20px',
                            fontSize: '10.5px',
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                            background: TOM_CAIXA.pendente.fundoCabecalho,
                            color: TOM_CAIXA.pendente.tintaCabecalho,
                          }}
                        >
                          pendente
                        </span>
                      )}
                      <span
                        style={{
                          flex: 'none',
                          padding: '2px 8px',
                          borderRadius: '20px',
                          fontSize: '10.5px',
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                          background: semMov ? 'oklch(0.96 0.006 160)' : C.amberBg,
                          color: semMov ? 'oklch(0.48 0.015 165)' : C.amberFg,
                        }}
                      >
                        {semMov ? 'sem movimento' : 'não consta'}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
            {companies && companies.length > 0 && (
              <div
                style={
                  pp
                    ? {
                        overflowY: 'auto',
                        flex: 1,
                        minHeight: 0,
                        padding: '8px 10px 14px',
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 460px), 1fr))',
                        alignContent: 'start',
                      }
                    : { overflowY: 'auto', padding: '8px 0' }
                }
              >
                {companies.map(pc => (
                  <div
                    key={pc.key}
                    style={{
                      padding: '10px 20px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                      borderBottom: '1px solid oklch(0.96 0.006 160)',
                      boxShadow: `inset 3px 0 0 ${pc.accent}`,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '10px',
                      }}
                    >
                      <NomeEmpresa
                        variante="bloco"
                        nome={pc.name}
                        codigo={pc.key}
                        style={{ fontSize: '13px', fontWeight: 600, minWidth: 0 }}
                      />
                      {/* Tag discreta da keep-rule de filtrarPorRecorte (revisão 09/09):
                      empresa da execução que o /resumo não traz — preservada de
                      propósito (dado vivo não some), mas rotulada. */}
                      {pc.foraDoResumo && (
                        <span
                          title="Empresa presente na execução mas ausente do /resumo (ex.: inativa ou fora de vigência) — mantida na lista porque dado vivo da execução não é escondido"
                          style={{
                            flex: 'none',
                            padding: '2px 7px',
                            borderRadius: '20px',
                            border: '1px solid oklch(0.88 0.008 160)',
                            background: 'oklch(0.97 0.004 160)',
                            color: 'oklch(0.55 0.015 165)',
                            fontSize: '10px',
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                            cursor: 'help',
                          }}
                        >
                          fora do resumo
                        </span>
                      )}
                      {/* Selo do historico (24/09): a empresa esta' resolvida HOJE mas
                      falhou antes na janela. O status virou o ultimo desfecho e ela
                      saiu do vermelho — este selo e' o que impede a correcao de
                      esconder que houve problema. */}
                      {/* Reconcilia o badge com a caixa: "Concluído" + caixa vermelha
                      parecia contradicao ate' a linha dizer que sao bots
                      diferentes. */}
                      {pc.resumoBots && (
                        <span
                          title="Esta empresa concluiu em alguns bots do grupo e falhou em outros — o badge mostra o status que venceu a dedup"
                          style={{
                            flex: 'none',
                            padding: '2px 7px',
                            borderRadius: '20px',
                            border: '1px solid oklch(0.88 0.008 160)',
                            background: 'oklch(0.97 0.004 160)',
                            color: 'oklch(0.5 0.015 165)',
                            fontSize: '10px',
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                            cursor: 'help',
                          }}
                        >
                          {pc.resumoBots}
                        </span>
                      )}
                      {pc.falhasAnteriores != null && (
                        <span
                          title={tituloFalhasAnteriores(
                            pc.falhasAnteriores,
                            pc.estadoDoSelo,
                            pc.ultimaFalhaEm,
                          )}
                          style={{
                            flex: 'none',
                            padding: '2px 7px',
                            borderRadius: '20px',
                            border: '1px solid oklch(0.85 0.06 75)',
                            background: 'oklch(0.97 0.025 85)',
                            color: 'oklch(0.52 0.09 65)',
                            fontSize: '10px',
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                            cursor: 'help',
                          }}
                        >
                          {/* Com data, o selo diz QUANDO (28/09); sem ela o texto e'
                          exatamente o de sempre. */}
                          {pc.diaUltimaFalha
                            ? `já falhou ${pc.falhasAnteriores}x · ${pc.diaUltimaFalha}`
                            : `já falhou ${pc.falhasAnteriores}x`}
                        </span>
                      )}
                      <div
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '3px 9px',
                          borderRadius: '20px',
                          background: pc.badgeBg,
                          color: pc.badgeFg,
                          fontSize: '11px',
                          fontWeight: 600,
                          flex: 'none',
                        }}
                      >
                        <span
                          style={{
                            width: '6px',
                            height: '6px',
                            borderRadius: '50%',
                            background: pc.badgeDot,
                          }}
                        />
                        {pc.badge}
                      </div>
                    </div>
                    {/* MACRO: erro e alerta viram "Falha de processamento" e AS DUAS
                    mensagens aparecem — esconder uma delas deixaria a caixa
                    contando metade da historia (143 pares tem as duas). Cada uma
                    mantem o seu tom e o seu atalho para a auditoria: o clique
                    precisa levar ao registro certo.

                    `&& pc.temMotivo` (25/09, pedido do dono): "se o bot retornou
                    sucesso para aquela empresa, marque ela como concluido no popup
                    e SEM retornar erros". O bloco da macro lia `motivoErro` direto,
                    sem olhar o status — entao a empresa CONCLUIDA aparecia com
                    caixa vermelha da mensagem que sobreviveu a dedup. `temMotivo`
                    ja' era a regra do caminho normal (`st === 'err' || 'warn'`) e
                    passa a valer nos dois: quem concluiu nao mostra erro.

                    O historico nao se perde — o selo "ja' falhou Nx" continua na
                    linha, que e' o lugar certo para dizer "houve problema antes"
                    sem afirmar que ha' problema agora. */}
                    {/* PENDENTE (28/09): o motivo do skip numa caixa propria, com o bot
                    de origem no cabecalho como nas de erro. SEM `onVer`: a aba
                    Erros nao tem registro de skip. Vale igual na macro e no
                    board/nano, com o mesmo rotulo.

                    Vem ANTES da caixa de ocorrencia em outro bot: a pendencia e'
                    o estado da linha, a ocorrencia e' o que aconteceu ao lado —
                    e cada caixa nomeia o seu bot. */}
                    {pc.motivoSkip && (
                      <CaixaMensagem
                        tom="pendente"
                        mensagem={pc.motivoSkip}
                        bot={pc.bot}
                        apelidoBot={pc.bot && apelidoDoBot ? apelidoDoBot(pc.bot) : null}
                        historico={pc.historicoNaCaixa}
                      />
                    )}
                    {dados.macro && pc.temMotivo
                      ? [
                          pc.motivoErro
                            ? { chave: 'erro' as const, texto: pc.motivoErro, erro: true }
                            : null,
                          pc.motivoAviso
                            ? { chave: 'aviso' as const, texto: pc.motivoAviso, erro: false }
                            : null,
                        ]
                          .filter(Boolean)
                          .map(m => (
                            <CaixaMensagem
                              key={m!.chave}
                              tom={m!.erro ? 'erro' : 'aviso'}
                              tituloCaixa={ROTULO_MACRO.falha}
                              mensagem={m!.texto}
                              rotulo={`Ver na auditoria de erros: ${pc.name}`}
                              bot={pc.bot}
                              apelidoBot={pc.bot && apelidoDoBot ? apelidoDoBot(pc.bot) : null}
                              onVer={
                                onVerNaAuditoria ? () => aoVerNaAuditoria(pc, m!.erro) : undefined
                              }
                            />
                          ))
                      : pc.temMotivo && (
                          <CaixaMensagem
                            tom={pc.isErro ? 'erro' : 'aviso'}
                            mensagem={pc.motivo}
                            rotulo={`Ver na auditoria de erros: ${pc.name}`}
                            bot={pc.bot}
                            apelidoBot={pc.bot && apelidoDoBot ? apelidoDoBot(pc.bot) : null}
                            onVer={
                              onVerNaAuditoria ? () => aoVerNaAuditoria(pc, pc.isErro) : undefined
                            }
                          />
                        )}
                    {/* Vencedor da dedup é outro status (ex.: concluiu no bot-b), mas há
                    erro/aviso em outro bot do grupo E a mensagem dele sobreviveu:
                    a caixa aparece no tom da OCORRÊNCIA, clicável para a auditoria.
                    O badge/estado da linha continua o do vencedor — só a mensagem,
                    que antes sumia calada na dedup, volta a ser visível. */}
                    {!pc.temMotivo && pc.ocorrenciaEmOutroBot && pc.motivoEmOutroBot && (
                      <CaixaMensagem
                        tom={pc.ocorrenciaEmOutroBot === 'err' ? 'erro' : 'aviso'}
                        tituloCaixa={dados.macro ? ROTULO_MACRO.falha : undefined}
                        mensagem={pc.motivoEmOutroBot}
                        rotulo={`Ver na auditoria de erros: ${pc.name}`}
                        bot={pc.botDaOcorrencia}
                        apelidoBot={
                          pc.botDaOcorrencia && apelidoDoBot
                            ? apelidoDoBot(pc.botDaOcorrencia)
                            : null
                        }
                        onVer={onVerNaAuditoria ? () => aoVerOcorrenciaDeOutroBot(pc) : undefined}
                      />
                    )}
                    {/* Sem mensagem preservada (ex.: o bot não mandou motivo): só o
                    atalho discreto, como desde 09/09. */}
                    {!pc.temMotivo &&
                      pc.ocorrenciaEmOutroBot &&
                      !pc.motivoEmOutroBot &&
                      onVerNaAuditoria && (
                        <button
                          type="button"
                          onClick={() => aoVerOcorrenciaDeOutroBot(pc)}
                          aria-label={`Ver na auditoria de erros: ${pc.name}`}
                          title="Há erro/aviso em outro bot deste grupo — ver na auditoria de erros"
                          style={{
                            alignSelf: 'flex-start',
                            padding: 0,
                            border: 'none',
                            background: 'none',
                            font: 'inherit',
                            fontSize: '11px',
                            fontWeight: 600,
                            color: 'oklch(0.5 0.016 165)',
                            cursor: 'pointer',
                            textAlign: 'left',
                          }}
                        >
                          ver na auditoria →
                        </button>
                      )}
                    {/* HISTORICO DA LINHA (05/10, pedido do dono): "junta as linhas
                    em uma so em ordem cronologica mostrando dd/MM/AA HH:mm:ss".

                    Exige BOT e PERIODO. Sem bot (popup de grupo, onde a linha
                    agrega N bots) a linha do tempo seria de um bot arbitrario, e
                    sem periodo ela falaria de um recorte diferente do no — nos
                    dois casos a tela se contradiria, entao o botao nem aparece.

                    Fechado por default e SOB DEMANDA: 70% das linhas tem
                    historico para mostrar, e carregar todas de uma vez somaria
                    ~1,4 MB a cada abertura do popup. */}
                    {(() => {
                      // O HISTORICO SEGUE O BOT DA CAIXA (07/10). `pc.bot` e' o
                      // vencedor da DEDUP; quando a linha foi promovida a falha por
                      // OUTRO bot do grupo ("passou em 2 · falhou em 1"), a caixa
                      // acima ja' exibe `botDaOcorrencia` — e o historico buscava o
                      // vencedor. Resultado medido em producao: caixa "Falha de
                      // processamento · Transmissao EFD Contribuicoes" com o
                      // historico logo abaixo dizendo "1 evento: Concluido". Os dois
                      // blocos, colados, falavam de bots DIFERENTES.
                      //
                      // `pc.bot` JA' resolve o fallback para `dados.botPadrao` na
                      // montagem da linha (acima) — repetir o `||` aqui era guarda
                      // morta: nenhum mutante conseguia distinguir as duas versões.
                      const botDaLinha = pc.botDaOcorrencia ?? pc.bot;
                      if (!botDaLinha || !periodo) return null;
                      const chaveHist = `${botDaLinha}|${pc.key}`;
                      const aberto = historicoAberto.has(chaveHist);
                      return (
                        <>
                          <button
                            type="button"
                            onClick={() => alternarHistorico(chaveHist)}
                            aria-expanded={aberto}
                            aria-label={`${aberto ? 'Ocultar' : 'Ver'} histórico cronológico: ${pc.name}`}
                            style={{
                              alignSelf: 'flex-start',
                              padding: 0,
                              border: 'none',
                              background: 'none',
                              font: 'inherit',
                              fontSize: '11px',
                              fontWeight: 600,
                              color: 'oklch(0.5 0.016 165)',
                              cursor: 'pointer',
                              textAlign: 'left',
                            }}
                          >
                            {aberto ? '▾ ocultar histórico' : '▸ ver histórico'}
                          </button>
                          {/* VER PRINT (13/10) — só no popup analítico e só em linha
                          com erro/alerta: é onde existe ocorrência com print. */}
                          {pp && (pc.isErro || pc.temMotivo || pc.ocorrenciaEmOutroBot) && (
                            <BotaoVerPrint
                              bot={botDaLinha}
                              codiEmp={pc.key}
                              de={periodo.de}
                              ate={periodo.ate}
                            />
                          )}
                          {aberto && (
                            <HistoricoDaUnidade
                              bot={botDaLinha}
                              codiEmp={pc.key}
                              de={periodo.de}
                              ate={periodo.ate}
                              aberto
                            />
                          )}
                        </>
                      );
                    })()}
                  </div>
                ))}
              </div>
            )}
            {!companies && !pp && (
              <div
                style={{
                  padding: '22px 20px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                <div style={{ fontSize: '28px', fontWeight: 700, letterSpacing: '-0.02em' }}>
                  {dados.count ?? 0}
                </div>
                <div style={{ fontSize: '12.5px', color: 'oklch(0.55 0.015 165)' }}>
                  empresa(s) neste ponto agora
                </div>
              </div>
            )}
          </div>
        </div>

        {dados.actionLabel && (
          <div style={{ padding: '14px 20px', borderTop: '1px solid oklch(0.93 0.008 160)' }}>
            <div
              onClick={dados.onAction}
              style={{
                padding: '10px 16px',
                borderRadius: '9px',
                background: 'oklch(0.22 0.026 168)',
                color: 'oklch(0.96 0.01 160)',
                fontSize: '13px',
                fontWeight: 600,
                textAlign: 'center',
                cursor: 'pointer',
              }}
            >
              {dados.actionLabel}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
